import { useCallback } from 'react'
import useApi from '@/hooks/useApi'
import { pubhubIndexedDb } from '@/utils/indexedDBHelper'
import { generalConstants } from '@tweddle-se-team/common/constants'

const usePublication = ({ params = {} } = {}) => {
  const apiHook = useApi()
  const isDraft = params.publicationState === generalConstants.PUBLICATION_STATES.DRAFT

  const getPublication = useCallback(
    async (id, requestParams = {}) => {
      // Named parameters, not a Mongo document (TB-11945). `params` carries publicationState, which is
      // part of the contract; anything outside the vocabulary now fails loudly with a 400 instead of
      // silently matching nothing.
      const query = new URLSearchParams({ contentRef: id, ...params, ...requestParams }).toString()
      const { items } = (await apiHook.get(`publications?${query}`)) || {}
      return (
        items && {
          cached: false,
          ...items[0]
        }
      )
    },
    [apiHook, params]
  )

  const loadPublication = useCallback(
    async (id, requestParams = {}) => {
      if (!isDraft) {
        const cached = await pubhubIndexedDb.publications.where('ditaId').equals(id).first()
        if (cached) {
          return {
            cached: true,
            ...cached.publicationFromApi,
            cachedImageUrl: cached.vehicleImageUrl
          }
        }
      }
      return await getPublication(id, requestParams)
    },
    [getPublication, isDraft]
  )

  const getRelatedPublicationsByLanguage = useCallback(
    async (id, publicationType) => {
      if (isDraft) return {}
      try {
        const query = publicationType ? `?publicationType=${encodeURIComponent(publicationType)}` : ''
        const items = (await apiHook.get(`publications/${id}/related-by-language${query}`)) || []
        return items.reduce((acc, item) => {
          if (item.persistentId) {
            acc[item.language] = item.persistentId
          }
          return acc
        }, {})
      } catch (error) {
        console.error('[usePublication] getRelatedPublicationsByLanguage failed:', error)
        return {}
      }
    },
    [apiHook, isDraft]
  )

  return {
    loadPublication,
    getPublication,
    getRelatedPublicationsByLanguage
  }
}

export default usePublication
